import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import { Link, navigate } from 'gatsby';
import { Input } from '../FormFields';
import Search from '../../assets/icons/search_input.svg';
import Chevron from '../../assets/icons/chevron_right.svg';
import { withTranslation } from 'react-i18next';
import MenuContext from '../../util/MenuContext'
import cn from 'classnames';
import Close from "../../assets/icons/close-menu.svg";
import { ScrollContext } from '../../util/ScrollContext';
import { CSSTransition } from 'react-transition-group';

const SearchWrapper = ({t, i18n, setSearchOverlay, autoFocus, preNavigate, isMobile, openSearch}) => {
    const [searchValue, setSearchValue] = useState('');
    const [validationMessage, setValidationMessage] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [dropDown, setDropDown] = useState(false);
    const [closeDropDown, setCloseDropDown] = useState(false);
    const {popular} = React.useContext(MenuContext);

    const [searchResults, setSearchResults] = useState(popular)
    const langcode = i18n.language
    const numberOfResults = 3
    const breakpointLarge = 1000

    useEffect(() => {
        if (submitted) {
            validate(searchValue);

            if (searchValue.length) {
                const searchParams = new URLSearchParams(window.location.search)
                preNavigate();

                // Gatsby won't redirect to the same page.
                if (searchValue === searchParams.get('term')) {
                    window.location.reload()
                } else {
                    navigate(`${t('search.url')}?term=${searchValue}`);
                }
            } else {
                setSubmitted(false)
            }
        }
        if (openSearch) {
            setDropDown(true)
        }

    }, [searchValue, submitted, openSearch])

    const onFocus = e => {
        setDropDown(true);
        if (e.length === 0) {
            setValidationMessage('');
        }
    }

    const onChange = async e => {
        const searchQuery = e.target.value
        setSearchValue(searchQuery);
        validate(searchQuery);

        if (!searchQuery.length) {
            setSearchResults(popular)
            return
        }
        const lunr = await window.__LUNR__.__loaded
        // Get exact matches
        let refs = _.slice(lunr[langcode.toLowerCase()].index.search(`${searchQuery}`), 0, numberOfResults)

        // Try to get matches with wildcard
        if (_.isEmpty(refs)) {
            refs = _.slice(lunr[langcode.toLowerCase()].index.search(`${searchQuery}*`), 0, numberOfResults)
        }

        if (_.isEmpty(refs)) {
            setValidationMessage(t('search_bar.no_results'));
            return
        }
        const pages = refs.map(({ref}) => lunr[langcode.toLowerCase()].store[ref]).map(page => {
            return {
                label: page.title,
                url: {
                    path: `${page.url}?hl=${searchQuery}`
                }
            }
        })
        setSearchResults(pages)
    };

    const onSubmit = e => {
        e.preventDefault();

        setSubmitted(true);
    };

    const validate = input => {
        if (input.length === 0) {
            setValidationMessage(t('search_bar.validation_message'));
        } else {
            setValidationMessage('');
        }
    };

    const searchbarId = isMobile ? 'zoeken-mobiel' : 'zoeken'
    return (
        <div className={cn({
            'desktop-search': !isMobile,
            'mobile-search': isMobile,
        })} id={searchbarId}>
            <form action="#" className="search-form" onSubmit={onSubmit}>
                <div className="search-wrapper">
                    <ScrollContext.Consumer>
                        {({toggleMenu}) => (
                            <Input
                                type="search"
                                label={t("search_bar.placeholder")}
                                idSuffix={isMobile ? '-mobile' : undefined}
                                autoFocus={autoFocus}
                                labelClass='sr-only'
                                placeholder={t("search_bar.placeholder")}
                                onChange={e => onChange(e)}
                                onFocus={e => {
                                    onFocus(e);
                                    !closeDropDown && toggleMenu(open);
                                }}
                                value={searchValue}
                            />
                        )}
                    </ScrollContext.Consumer>


                    <button type="submit" className="search-button">
                        <span className="sr-only">{t("search_bar.search")}</span>
                        <Search className="search-icon"/>
                    </button>
                </div>
                <CSSTransition
                    in={dropDown}
                    timeout={300}
                    unmountOnExit
                >
                    <div className="dropdown-wrapper">
                        <ScrollContext.Consumer>
                            {({toggleMenu}) => (
                                <div className="dropdown-overlay"
                                     onClick={() => {
                                         setSearchOverlay(false);
                                         setDropDown(false);
                                         isMobile && setCloseDropDown(true);
                                         toggleMenu(!open);
                                     }}
                                     onKeyDown={(e) => {
                                         // Escape
                                         if (e.keyCode === 27) {
                                             setSearchOverlay(false);
                                             setDropDown(false);
                                             toggleMenu(!open);
                                         }
                                     }}/>
                            )}
                        </ScrollContext.Consumer>

                        <div className="dropdown-content-wrapper">
                            <div role="status" className={`search-message${validationMessage && ' active'}`}>
                                {validationMessage}
                            </div>

                            {(!_.isEmpty(searchValue) && !_.isEmpty(searchResults)) && (
                                    <div className="dropdown-title">{t('search_bar.suggestions')}</div>
                                )
                            }

                            <ul className="dropdown">
                                {(!_.isEmpty(searchValue) && !_.isEmpty(searchResults)) &&
                                    (<li className="dropdown-item orange">
                                        <Link onClick={() => setSearchOverlay(false)} to={`${t('search.url')}?term=${searchValue}`}>
                                            <Chevron/>
                                            <span>{t('search_bar.more_results')}</span>
                                        </Link>
                                    </li>
                                    )
                                }
                                {searchResults.map((item, index) => (
                                <li className="dropdown-item" key={uuid()}>
                                        <Link
                                            to={item.url.path}
                                            onClick={() => setSearchOverlay(false)}
                                            onKeyDown={(e) => {
                                                if (window.innerWidth >= breakpointLarge && index === numberOfResults - 1) {
                                                    const shouldBlur = e.key === 'Tab' && !e.shiftKey

                                                    if (shouldBlur) {
                                                        e.preventDefault();
                                                        setSearchOverlay(false);
                                                        setDropDown(false);
                                                        isMobile && setCloseDropDown(true);
                                                    }
                                                }
                                            }}
                                        >
                                            <Chevron/>
                                            <span>{item.label}</span>
                                        </Link>
                                    </li>
                                ))}
                                <ScrollContext.Consumer>
                                {({toggleMenu}) => (
                                        <button className="dropdown-close"
                                            onClick={() => {
                                                setSearchOverlay(false);
                                                setDropDown(false);
                                                isMobile && setCloseDropDown(true);
                                                toggleMenu(!open);
                                            }}
                                            onKeyDown={(e) => {
                                                const shouldBlur = e.key === 'Tab' && !e.shiftKey

                                                if (shouldBlur) {
                                                    e.preventDefault();
                                                    setSearchOverlay(false);
                                                    setDropDown(false);
                                                    isMobile && setCloseDropDown(true);
                                                    toggleMenu(!open);
                                                }
                                            }}
                                        >
                                            <Close className="close-icon"/>
                                            <span>{t('search_bar.close')}</span>
                                        </button>
                                    )}
                                </ScrollContext.Consumer>
                            </ul>
                        </div>
                    </div>
                </CSSTransition>
            </form>
        </div>
    );
};

SearchWrapper.defaultProps = {
    autoFocus: true,
    preNavigate: () => {
    },
    isMobile: false,
};

SearchWrapper.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
    setSearchOverlay: PropTypes.func,
    preNavigate: PropTypes.func,
};

export default withTranslation()(SearchWrapper);
